import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, Copy, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { MFABackupCodes } from './MFABackupCodes';

interface MFAEnrollmentProps {
  onEnrollmentComplete: () => void;
}

export function MFAEnrollment({ onEnrollmentComplete }: MFAEnrollmentProps) {
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<{
    id: string;
    type: string;
    totp: { qr_code: string; secret: string; uri: string };
  } | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const startEnrollment = async () => {
    setIsEnrolling(true);
    setError('');
    
    try {
      // First, check for and clean up any existing factors
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      if (existingFactors?.totp && existingFactors.totp.length > 0) {
        // Unenroll all existing factors
        for (const factor of existingFactors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Now start fresh enrollment
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });

      if (enrollError) throw enrollError;
      
      setEnrollmentData(data);
    } catch (err) {
      console.error('MFA enrollment error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start enrollment');
    } finally {
      setIsEnrolling(false);
    }
  };

  const copySecret = () => {
    if (enrollmentData?.totp.secret) {
      navigator.clipboard.writeText(enrollmentData.totp.secret);
      setSecretCopied(true);
      toast({
        title: 'Secret copied',
        description: 'The secret key has been copied to your clipboard',
      });
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  const generateBackupCodes = () => {
    const codes: string[] = [];
    for (let i = 0; i < 10; i++) {
      const code = Array.from({ length: 12 }, () => 
        '0123456789ABCDEFGHJKMNPQRSTVWXYZ'[Math.floor(Math.random() * 32)]
      ).join('');
      const formatted = `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8, 12)}`;
      codes.push(formatted);
    }
    return codes;
  };

  const hashCode = async (code: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(code.replace(/-/g, ''));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const verifyCode = async () => {
    if (!enrollmentData || !verificationCode) return;
    
    setIsVerifying(true);
    setError('');

    try {
      // For enrollment verification, we create a challenge then immediately verify
      // This completes the enrollment process
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollmentData.id,
      });

      if (challengeError) {
        console.error('Challenge error:', challengeError);
        throw challengeError;
      }

      if (!challengeData) {
        throw new Error('No challenge data returned');
      }

      console.log('Challenge created, verifying code...');

      // Now verify with the code from the authenticator app
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollmentData.id,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) {
        console.error('Verify error:', verifyError);
        throw verifyError;
      }

      console.log('Verification successful:', verifyData);

      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('User not found');

      // Generate backup codes
      const backupCodes = generateBackupCodes();
      const codeHashes = await Promise.all(backupCodes.map(hashCode));

      // Store backup codes
      const { error: codesError } = await supabase.rpc('generate_mfa_backup_codes', {
        p_user_id: userId,
        p_code_hashes: codeHashes,
      });

      if (codesError) throw codesError;

      // Update profile to mark MFA as enabled
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          mfa_enabled: true,
          mfa_enrolled_at: new Date().toISOString(),
        })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      // Send notification
      await invokeEdgeFunction('mfa-notifications', {
        body: {
          type: 'enrollment',
          userId,
        },
        callerOperation: 'MFAEnrollment.enrollNotify',
      });

      toast({
        title: 'Two-factor authentication enabled',
        description: 'Save your backup codes in a secure location',
      });

      // Show backup codes before completing
      setBackupCodes(backupCodes);
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setIsVerifying(false);
    }
  };

  if (backupCodes) {
    return (
      <MFABackupCodes
        codes={backupCodes}
        onClose={() => {
          setBackupCodes(null);
          onEnrollmentComplete();
        }}
      />
    );
  }

  if (!enrollmentData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Enable Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              You'll need an authenticator app like Google Authenticator, Authy, or 1Password to set up 2FA.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={startEnrollment}
            disabled={isEnrolling}
            className="w-full"
          >
            {isEnrolling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Setting up...
              </>
            ) : (
              'Start Setup'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scan QR Code</CardTitle>
        <CardDescription>
          Use your authenticator app to scan this QR code
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* QR Code */}
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-lg">
            <img 
              src={enrollmentData.totp.qr_code} 
              alt="QR Code for 2FA setup"
              className="w-48 h-48"
            />
          </div>
        </div>

        {/* Manual Entry Option */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            Or enter this code manually:
          </Label>
          <div className="flex gap-2">
            <Input
              value={enrollmentData.totp.secret}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={copySecret}
              title="Copy secret"
            >
              {secretCopied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Verification */}
        <div className="space-y-2">
          <Label htmlFor="verification-code">
            Enter 6-digit code from your app
          </Label>
          <Input
            id="verification-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            className="text-center text-2xl tracking-widest font-mono"
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          onClick={verifyCode}
          disabled={isVerifying || verificationCode.length !== 6}
          className="w-full"
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify and Enable'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
